import Vue from "vue";
import Guid from "@/app/types/common/guid";

export interface BmGridColumnSortState {
  columnKey: string;
  descending?: boolean;
}

export type GridExportFormat = "csv" | "pdf" | "googleSheets";

export interface BmGridExportEvent {
  filename: string;
  fileTitle: string;
  stateId: string;
  format: GridExportFormat;
}

export class BmGridState {
  // Whether the grid should use the default state. Set this to true to reset the
  // grid state back to the default grid state as defined by the column definitions.
  useGridDefault: boolean = false;

  // Ranked array of sort criteria to apply.
  sortBy: BmGridColumnSortState[] = [];

  // Ranked array of groupings to apply.
  groupBy: string[] = [];

  // The state of the applied column filters
  columnFilters: { [columnKey: string]: any } = {};

  removedColumnKeys: string[] = [];

  columnOrder: string[] = [];

  gridSearchFilter: string = "";

  stateId: string;

  columnPixelWidthMap = new Map<string, number>();

  public constructor(state?: Partial<BmGridState>) {
    if (!state) {
      this.useGridDefault = true;
    } else {
      Object.assign(this, state || {});
    }
    this.stateId = state?.stateId || Guid.create().toString();
  }

  /**
   * Resets the grid state to indicate that the grid should use the default (as
   * defined by the column definitions).
   */
  public reset() {
    this.sortBy = [];
    this.groupBy = [];
    this.columnFilters = {};
    this.removedColumnKeys = [];
    this.columnOrder = [];
    this.gridSearchFilter = "";
    this.useGridDefault = true;
    this.columnPixelWidthMap = new Map<string, number>();
  }

  public deleteFilter(key: string) {
    Vue.delete(this.columnFilters, key);
  }

  /**
   * Applies a new column sort to the existing sort state. I have borrowed this logic from
   * ag-grid (ascend, descend, remove + stacking). https://www.ag-grid.com/
   *
   * @param columnKey The column key being sorted
   * @param stack Whether to stack this on to the existing sort state or not.
   */
  applyColumnSort(columnKey: string, stack: boolean = false): void {
    const sortByIndex = this.sortBy.findIndex(
      (sortBy) => sortBy.columnKey === columnKey
    );

    if (sortByIndex !== -1) {
      if (!this.sortBy[sortByIndex]?.descending) {
        // adjust descending state
        Vue.set(this.sortBy[sortByIndex], "descending", true);
        if (!stack) {
          this.sortBy = [this.sortBy[sortByIndex]];
        }
      } else {
        // add new sort state
        if (stack) {
          this.sortBy.splice(sortByIndex, 1); // remove stacked item
        } else {
          this.sortBy = [];
        }
      }
    } else {
      // adding new
      if (stack) {
        this.sortBy.push({ columnKey });
      } else {
        this.sortBy = [{ columnKey }];
      }
    }
  }

  applyExternalSorting(sortStatus: BmGridColumnSortState[]): void {
    this.sortBy = sortStatus;
  }

  applyColumnOrder(columnOrder: string[]) {
    this.columnOrder = columnOrder;
  }

  setColumnPixelWidth(key: string, pixels: number) {
    this.columnPixelWidthMap.set(key, pixels);
  }
}
